import { z } from "zod";
import type { Game } from "./types";
export const perfNames = [
  "ultraBullet",
  "bullet",
  "blitz",
  "rapid",
  "classical",
  "correspondence",
  "chess960",
  "crazyhouse",
  "antichess",
  "atomic",
  "horde",
  "kingOfTheHill",
  "racingKings",
  "threeCheck",
] as const;
export const syncSections = [
  "profile",
  "games",
  "ratingHistory",
  ...perfNames.map((p) => `perf:${p}`),
  "activity",
  "tournaments",
  "teams",
  "fide",
  "fideHistory",
  "storm",
  "puzzle30",
  "puzzle365",
  "puzzleAll",
  "puzzleActivity",
  "studies",
];
const sectionLabels: Record<string, string> = {
  profile: "Account profile and ratings",
  games: "Completed games",
  olderGames: "Older completed games",
  ratingHistory: "Rating history",
  activity: "Chess activity",
  tournaments: "Arena tournament results",
  teams: "Chess teams",
  fide: "Linked FIDE profile",
  fideHistory: "FIDE rating history",
  storm: "Puzzle Storm records",
  puzzle30: "Puzzle results · 30 days",
  puzzle365: "Puzzle results · one year",
  puzzleAll: "Puzzle results · lifetime",
  puzzleActivity: "Recent puzzle attempts",
  studies: "Study names and dates",
};
export function lichessSectionLabel(section: string) {
  return sectionLabels[section] ?? section.replace("perf:", "Performance · ");
}
export function stopsLichessSync(status: number) {
  return [401, 409, 429].includes(status);
}
export const LichessSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9_-]{1,40}$/),
    username: z.string().max(40),
    connectedAt: z.number(),
    useForCoaching: z.boolean(),
    sections: z.record(
      z.string().max(40),
      z.object({ at: z.number(), data: z.string().max(300_000) }),
    ),
    oldestGameAt: z.number().optional(),
    newestGameAt: z.number().optional(),
    historyComplete: z.boolean().optional(),
    lastError: z.string().max(300).optional(),
  })
  .refine(
    (v) => JSON.stringify(v).length <= 900_000,
    "Lichess statistics exceed the storage limit.",
  );
export type LichessProfile = z.infer<typeof LichessSchema>;
export const LichessGameSource = z.object({
  provider: z.literal("lichess"),
  id: z.string().regex(/^[a-zA-Z0-9]{8}$/),
  username: z.string().max(40),
  perf: z.string().max(40),
  rated: z.boolean(),
  details: z.string().max(25000),
});
export type Json = z.infer<ReturnType<typeof z.json>>;
export function lichessSummary(
  link: LichessProfile | undefined,
  imported: number | Game[],
) {
  if (!link?.useForCoaching) return null;
  const profile = sectionData(link, "profile") as
    Record<string, Json> | undefined;
  const dashboard = sectionData(link, "puzzle30") as
    | {
        global?: Json;
        themes?: Record<
          string,
          { results?: { nb?: number; firstWins?: number }; theme?: string }
        >;
      }
    | undefined;
  const themes = Object.entries(dashboard?.themes ?? {})
    .map(([key, value]) => ({
      theme: value.theme ?? key,
      attempts: value.results?.nb ?? 0,
      firstWins: value.results?.firstWins ?? 0,
    }))
    .filter((v) => v.attempts >= 5)
    .sort((a, b) => a.firstWins / a.attempts - b.firstWins / b.attempts);
  const gameList =
    typeof imported === "number"
      ? []
      : imported.filter(
          (g) =>
            g.source?.provider === "lichess" &&
            g.source.username.toLowerCase() === link.username.toLowerCase(),
        );
  const careers = perfNames.flatMap((perf) => {
    const data = record(sectionData(link, `perf:${perf}`)),
      stat = record(data.stat);
    return Object.keys(data).length
      ? [
          {
            perf,
            rank: data.rank,
            percentile: data.percentile,
            highest: stat.highest,
            lowest: stat.lowest,
            results: stat.count,
            streaks: {
              currentWins: stat.resultStreak?.win?.cur?.v,
              longestWins: stat.resultStreak?.win?.max?.v,
              currentLosses: stat.resultStreak?.loss?.cur?.v,
              longestLosses: stat.resultStreak?.loss?.max?.v,
            },
          },
        ]
      : [];
  });
  const histories = sectionData(link, "ratingHistory");
  const trends = Array.isArray(histories)
    ? histories.map((value) => {
        const series = record(value),
          points = Array.isArray(series.points) ? series.points : [];
        return {
          ratingCategory: series.name,
          firstRecorded: points[0],
          latestRecorded: points.at(-1),
          recentPoints: points.slice(-5),
          totalPoints: points.length,
        };
      })
    : [];
  const tournamentData = sectionData(link, "tournaments"),
    teams = sectionData(link, "teams"),
    studies = sectionData(link, "studies");
  return {
    source: "Lichess",
    username: link.username,
    ratings: profile?.perfs,
    profileBackground: profile?.profile,
    accountCreatedAt: profile?.createdAt,
    title: profile?.title,
    linkedFideProfile: sectionData(link, "fide"),
    careerPerformance: careers,
    ratingTrends: trends,
    gamePatterns: summarizeLichessGames(gameList),
    recentTournamentResults: Array.isArray(tournamentData)
      ? tournamentData.slice(0, 8)
      : null,
    teams: Array.isArray(teams)
      ? teams.map((v) => {
          const t = record(v);
          return { id: t.id, name: t.name, members: t.nbMembers };
        })
      : null,
    studyInterests: Array.isArray(studies)
      ? { cached: studies.length, sample: studies.slice(0, 20) }
      : null,
    puzzlesLastYear: record(sectionData(link, "puzzle365")).global,
    puzzlesLifetime: record(sectionData(link, "puzzleAll")).global,
    stormHighScores: record(sectionData(link, "storm")).high,
    counts: profile?.count,
    playTime: profile?.playTime,
    puzzlesLast30Days: dashboard?.global,
    puzzleThemesToExplore: themes.slice(0, 8),
    importedCompletedStandardGames:
      typeof imported === "number" ? imported : gameList.length,
    historyComplete: link.historyComplete ?? false,
    availableSections: Object.entries(link.sections).map(([section, v]) => ({
      section,
      syncedAt: v.at,
    })),
    limitations:
      "Ratings are Lichess-specific. Imported games and recent puzzle activity are samples; use aggregate statistics for totals. Missing data is unknown. Read cached sections for detailed history and performance; no ongoing games are accessible.",
  };
}

export function sectionData(
  link: LichessProfile | undefined,
  section: string,
): unknown {
  try {
    return JSON.parse(link?.sections[section]?.data ?? "null");
  } catch {
    return null;
  }
}

function record(value: unknown): Record<string, any> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, any>)
    : {};
}
export function summarizeLichessGames(games: Game[]) {
  const groups = new Map<
    string,
    {
      opening: string;
      color: string;
      games: number;
      wins: number;
      draws: number;
      losses: number;
    }
  >();
  const sides = {
    w: { games: 0, wins: 0, draws: 0, losses: 0 },
    b: { games: 0, wins: 0, draws: 0, losses: 0 },
  };
  let analyzed = 0,
    accuracy = 0;
  for (const game of games) {
    const username = game.source?.username.toLowerCase();
    const color =
      game.headers.White?.toLowerCase() === username
        ? "w"
        : game.headers.Black?.toLowerCase() === username
          ? "b"
          : game.humanColor;
    // Workspace analysis/takebacks must not rewrite the original game result.
    const result = game.headers.Result ?? game.result;
    if (!["1-0", "0-1", "1/2-1/2"].includes(result)) continue;
    const outcome =
      result === "1/2-1/2"
        ? "draws"
        : result === (color === "w" ? "1-0" : "0-1")
          ? "wins"
          : "losses";
    sides[color].games++;
    sides[color][outcome]++;
    const opening = game.headers.Opening || game.headers.ECO;
    if (opening) {
      const key = `${color}:${opening}`;
      const item = groups.get(key) ?? {
        opening,
        color,
        games: 0,
        wins: 0,
        draws: 0,
        losses: 0,
      };
      item.games++;
      item[outcome]++;
      groups.set(key, item);
    }
    try {
      const details = JSON.parse(game.source?.details ?? "{}");
      const value =
        details.players?.[color === "w" ? "white" : "black"]?.analysis
          ?.accuracy;
      if (typeof value === "number" && Number.isFinite(value)) {
        accuracy += value;
        analyzed++;
      }
    } catch {
      /* Missing platform analysis is unknown. */
    }
  }
  return {
    source:
      "Imported completed standard games only; a sample, not career totals",
    sampleSize: games.length,
    byColor: sides,
    commonOpenings: [...groups.values()]
      .sort((a, b) => b.games - a.games)
      .slice(0, 12),
    platformAccuracy: analyzed
      ? {
          average: Math.round((accuracy / analyzed) * 10) / 10,
          gamesWithAnalysis: analyzed,
        }
      : null,
  };
}
